<template>
    <div>
        <b-form-group
            id="fieldset-telegram-auth"
            label-cols-sm="4"
            label-cols-lg="3"
            content-cols-sm
            content-cols-lg="7"
            label-for="telegram-auth"
            :description="$t('settings.channels.telegram.description')"
            :label="$t('settings.channels.telegram.token')"
        >
            <b-form-input id="telegram-auth" v-model="token" />
        </b-form-group>

        <b-form-group label-cols-sm="4" label-cols-lg="3" content-cols-sm content-cols-lg="7">
            <div v-if="!isLoading">
                <b-button variant="primary" @click="setBotToken">
                    {{ $t("settings.channels.telegram.connect") }}
                </b-button>

                <b-button
                    v-if="channelData.identity !== 'disconnected'"
                    variant="light"
                    class="ml-1"
                    @click="disconnect"
                >
                    {{ $t("settings.channels.telegram.disconnect.button") }}
                </b-button>
            </div>
            <b-spinner v-else variant="primary" label="Spinning" />
        </b-form-group>
    </div>
</template>

<script>
import { ref } from "@vue/composition-api";
import { BButton, BButtonGroup, BCard, BSpinner, BFormGroup, BFormInput } from "bootstrap-vue";
import axios from "@/libs/axios";
import { useToast } from "vue-toastification/composition";
import i18n from "@/libs/i18n";

export default {
    components: {
        BCard,
        BFormGroup,
        BFormInput,
        BButton,
        BButtonGroup,
        BSpinner,
    },
    props: {
        channelData: {
            type: Object,
            required: true,
        },
    },
    setup({ channelData }) {
        const toast = useToast();
        const token = ref("");
        const isLoading = ref(false);

        try {
            token.value = JSON.parse(channelData.auth).token;
        } catch {
            // Do nothing if auth is invalid  and let token be an empty string
        }

        const setBotToken = async () => {
            isLoading.value = true;
            const bot = await axios
                .get(`https://api.telegram.org/bot${token.value}/getMe`)
                .then((result) => result.data)
                .catch(() => null);

            if (!bot?.ok) {
                toast.error(i18n.t("settings.channels.telegram.error"));
                isLoading.value = false;
                return;
            }

            if (channelData.identity !== "disconnected") {
                await axios.post(`channels/${channelData.id}/disconnect`).catch(() => null);
            }

            const auth = JSON.stringify({
                token: token.value,
            });

            const identity = bot.result.username;

            const updateResult = await axios
                .patch(`channels/${channelData.id}`, {
                    identity,
                    auth,
                })
                .catch(() => {
                    toast.error(i18n.t("settings.channels.telegram.error"));
                    isLoading.value = false;
                });

            if (!updateResult) {
                return;
            }

            await axios
                .post(`channels/${channelData.id}/connect`)
                .then(() => {
                    channelData.auth = auth;
                    channelData.identity = identity;
                    toast.success(i18n.t("settings.channels.telegram.success"));
                })
                .catch(() => {
                    toast.error(i18n.t("settings.channels.telegram.error"));
                })
                .finally(() => {
                    isLoading.value = false;
                });
        };

        const disconnect = async () => {
            isLoading.value = true;

            const auth = JSON.stringify({ token: "" });
            const identity = "disconnected";

            await axios.post(`channels/${channelData.id}/disconnect`).catch(() => null);

            await axios
                .patch(`channels/${channelData.id}`, {
                    identity,
                    auth,
                })
                .then(() => {
                    channelData.auth = auth;
                    channelData.identity = identity;
                    token.value = "";
                    toast.success(i18n.t("settings.channels.telegram.disconnect.success"));
                })
                .catch(() => {
                    toast.error(i18n.t("settings.channels.telegram.disconnect.error"));
                })
                .finally(() => {
                    isLoading.value = false;
                });
        };

        return {
            token,
            setBotToken,
            disconnect,
            isLoading,
        };
    },
};
</script>
